// Firestore Database Schema for SwipeSage

// User Collection
export interface User {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  phoneNumber?: string;
  createdAt: Date;
  updatedAt: Date;
  isAdmin: boolean;
  isActive: boolean;
  
  // Profile Information
  profile: {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: Date;
    gender?: 'male' | 'female' | 'other';
    occupation?: string;
    annualIncome?: number;
    creditScore?: number;
    spendingCategories?: string[];
    existingCards?: string[];
  };
  
  // Preferences
  preferences: {
    notifications: {
      email: boolean;
      push: boolean;
      sms: boolean;
    };
    theme: 'light' | 'dark' | 'system';
    language: string;
  };
  
  // Referral System
  referral: {
    referralCode: string;
    referredBy?: string;
    referralCount: number;
    totalEarnings: number;
    referralHistory: ReferralHistory[];
  };
}

// Credit Card Collection
export interface CreditCard {
  id: string;
  name: string;
  bank: string;
  category: string;
  cardType: 'credit' | 'debit' | 'prepaid';
  
  // Basic Information
  description: string;
  features: string[];
  eligibility: string[];
  imageUrl: string;
  logoUrl: string;
  
  // Financial Details
  annualFee: number;
  joiningFee: number;
  interestRate: number;
  creditLimit: {
    min: number;
    max: number;
  };
  
  // Rewards & Benefits
  rewards: {
    cashbackRate: number;
    rewardPoints: number;
    welcomeBonus: number;
    categories: RewardCategory[];
  };
  
  // Fees & Charges
  fees: {
    annualFee: number;
    joiningFee: number;
    latePaymentFee: number;
    cashAdvanceFee: number;
    foreignTransactionFee: number;
  };
  
  // MITC Data (Most Important Terms & Conditions)
  mitc?: {
    joiningFee: string;
    annualFee: string;
    interestRate: string;
    latePaymentCharges: string;
    foreignTransactionFee: string;
    rewardRate: string;
    cashbackRate: string;
    fuelSurchargeWaiver: string;
    interestFreePeriod: string;
    addOnCardFee: string;
    cashWithdrawalFee: string;
    overLimitFee: string;
    duplicateStatementFee: string;
    cardReplacementFee: string;
    emiConversionCharges: string;
    gstApplicable: string;
    sourceUrl: string;
    lastUpdated: Date;
    // Admin referral link (prioritized over sourceUrl)
    referralLink?: {
      url: string;
      notes?: string;
      addedBy?: string;
      addedAt?: Date;
    };
  };
  
  // Application Details
  application: {
    minIncome: number;
    minAge: number;
    maxAge: number;
    employmentType: string[];
    creditScore: {
      min: number;
      max: number;
    };
    documents: string[];
    processingTime: string;
    approvalRate: number;
  };
  
  // Analytics
  analytics: {
    views: number;
    applications: number;
    conversions: number;
    rating: number;
    reviewCount: number;
  };
  
  // Metadata
  isActive: boolean;
  isFeatured: boolean;
  tags: string[];
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

// Reward Category
export interface RewardCategory {
  category: string;
  rate: number;
  description: string;
  maxRewards?: number;
}

// User Card Applications
export interface CardApplication {
  id: string;
  userId: string;
  cardId: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  
  // Application Details
  applicationData: {
    personalInfo: {
      firstName: string;
      lastName: string;
      dateOfBirth: Date;
      phoneNumber: string;
      email: string;
    };
    employment: {
      employer: string;
      designation: string;
      annualIncome: number;
      employmentType: string;
    };
    address: {
      current: Address;
      permanent?: Address;
    };
    documents: {
      panCard: string;
      aadharCard: string;
      incomeProof: string;
      addressProof: string;
    };
  };
  
  // Tracking
  appliedAt: Date;
  updatedAt: Date;
  processedAt?: Date;
  approvedAt?: Date;
  rejectedAt?: Date;
  rejectionReason?: string;
  
  // Referral
  referredBy?: string;
  referralCode?: string;
}

// Address
export interface Address {
  street: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
}

// Referral History
export interface ReferralHistory {
  id: string;
  referredUserId: string;
  referredUserEmail: string;
  status: 'pending' | 'completed' | 'cancelled';
  commission: number;
  createdAt: Date;
  completedAt?: Date;
}

// User Activity
export interface UserActivity {
  id: string;
  userId: string;
  type: 'login' | 'card_view' | 'card_apply' | 'chat_message' | 'referral';
  data: any;
  timestamp: Date;
  sessionId?: string;
}

// Chat Messages
export interface ChatMessage {
  id: string;
  userId: string;
  sessionId: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
  metadata?: {
    intent?: string;
    confidence?: number;
    suggestedCards?: string[];
  };
}

// Chat Session
export interface ChatSession {
  id: string;
  userId: string;
  startedAt: Date;
  endedAt?: Date;
  messageCount: number;
  summary?: string;
  tags?: string[];
}

// Analytics Events
export interface AnalyticsEvent {
  id: string;
  userId?: string;
  event: string;
  properties: Record<string, any>;
  timestamp: Date;
  sessionId?: string;
  platform: 'web' | 'mobile';
  userAgent?: string;
  ipAddress?: string;
}

// System Settings
export interface SystemSettings {
  id: string;
  key: string;
  value: any;
  description?: string;
  updatedAt: Date;
  updatedBy: string;
}

// Notification
export interface Notification {
  id: string;
  userId: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  data?: any;
  isRead: boolean;
  createdAt: Date;
  readAt?: Date;
}

// Collection Names
export const COLLECTIONS = {
  USERS: 'users',
  CREDIT_CARDS: 'credit_cards',
  CARD_APPLICATIONS: 'card_applications',
  USER_ACTIVITIES: 'user_activities',
  CHAT_MESSAGES: 'chat_messages',
  CHAT_SESSIONS: 'chat_sessions',
  ANALYTICS_EVENTS: 'analytics_events',
  SYSTEM_SETTINGS: 'system_settings',
  NOTIFICATIONS: 'notifications',
} as const;

// Indexes for better query performance
export const FIRESTORE_INDEXES = {
  // Users
  'users_email': ['email'],
  'users_referralCode': ['referral.referralCode'],
  'users_createdAt': ['createdAt'],
  
  // Credit Cards
  'credit_cards_category': ['category'],
  'credit_cards_bank': ['bank'],
  'credit_cards_isActive': ['isActive'],
  'credit_cards_isFeatured': ['isFeatured'],
  'credit_cards_analytics_views': ['analytics.views'],
  'credit_cards_analytics_rating': ['analytics.rating'],
  
  // Card Applications
  'card_applications_userId': ['userId'],
  'card_applications_cardId': ['cardId'],
  'card_applications_status': ['status'],
  'card_applications_appliedAt': ['appliedAt'],
  
  // User Activities
  'user_activities_userId_timestamp': ['userId', 'timestamp'],
  'user_activities_type_timestamp': ['type', 'timestamp'],
  
  // Chat Messages
  'chat_messages_sessionId_timestamp': ['sessionId', 'timestamp'],
  'chat_messages_userId_timestamp': ['userId', 'timestamp'],
  
  // Analytics Events
  'analytics_events_timestamp': ['timestamp'],
  'analytics_events_event_timestamp': ['event', 'timestamp'],
} as const; 